import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { clearUserSession } from '../Auth/userSession';
import { changePassword } from './myPageApi';

const PASSWORD_RULE = '공백 없이 8자 이상, 영문·숫자·특수문자 중 2종류 이상을 포함해주세요.';
const emptyValues = { currentPassword: '', password: '', passwordConfirm: '' };

// 회원가입과 같은 비밀번호 기준(8자 이상, 공백 없음, 2종류 이상 조합)을 만족하는지 확인한다.
function isValidPassword(password) {
    return Array.from(password).length >= 8
        && !/\s/.test(password)
        && [/[a-z]/i, /[0-9]/, /[!-/:-@[-`{-~]/].filter(pattern => pattern.test(password)).length >= 2;
}

// 비밀번호 변경 입력값을 검증하고 필드별 오류 문구를 돌려준다.
function validatePassword(values) {
    return {
        currentPassword: values.currentPassword ? '' : '현재 비밀번호를 입력해주세요.',
        password: !isValidPassword(values.password) ? PASSWORD_RULE
            : values.password === values.currentPassword ? '현재 비밀번호와 다른 비밀번호를 입력해주세요.' : '',
        passwordConfirm: values.password !== values.passwordConfirm ? '비밀번호가 일치하지 않아요.' : '',
    };
}

// 비밀번호 변경 폼. 성공하면 모든 기기에서 로그아웃되므로 세션을 지우고 로그인 페이지로 보낸다.
export default function PasswordForm({ onCancel }) {
    const navigate = useNavigate();
    const [values, setValues] = useState(emptyValues);
    const [touched, setTouched] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const errors = validatePassword(values);
    const formValid = Object.values(errors).every(message => !message);

    // 입력란 변경 값을 상태에 반영한다.
    function change(event) {
        const { name, value } = event.target;
        setValues(previous => ({ ...previous, [name]: value }));
    }

    // 입력란을 벗어나면 해당 필드의 오류를 보이게 한다.
    function touch(event) {
        const { name } = event.target;
        setTouched(previous => ({ ...previous, [name]: true }));
    }

    // 필드에 오류가 있고 사용자가 건드렸을 때만 오류 문구를 보여준다.
    function fieldError(name) {
        return touched[name] && errors[name] ? <small className="my-page-field-error" role="alert">{errors[name]}</small> : null;
    }

    // 비밀번호 변경을 요청하고, 성공하면 재로그인을 안내한다.
    async function submit(event) {
        event.preventDefault();
        setTouched({ currentPassword: true, password: true, passwordConfirm: true });
        if (!formValid || submitting) return;
        setSubmitting(true);
        setError('');
        try {
            await changePassword({ currentPassword: values.currentPassword, password: values.password });
        } catch (submitError) {
            setError(submitError.message);
            setSubmitting(false);
            return;
        }
        // 서버가 refresh 토큰을 모두 폐기하고 쿠키를 지웠으므로 프론트 세션도 정리한다.
        window.alert('비밀번호를 변경했습니다. 새 비밀번호로 다시 로그인해주세요.');
        navigate('/login', { replace: true });
        clearUserSession();
    }

    return <form className="my-page-form my-page-inline-form" onSubmit={submit} noValidate>
        <label><span>현재 비밀번호</span><input type="password" name="currentPassword" value={values.currentPassword} onChange={change} onBlur={touch} disabled={submitting} required maxLength={72} autoComplete="current-password" aria-invalid={Boolean(touched.currentPassword && errors.currentPassword)} />{fieldError('currentPassword')}</label>
        <label><span>새 비밀번호</span><input type="password" name="password" value={values.password} onChange={change} onBlur={touch} disabled={submitting} required minLength={8} maxLength={72} autoComplete="new-password" aria-invalid={Boolean(touched.password && errors.password)} />{touched.password && errors.password ? fieldError('password') : <small>{PASSWORD_RULE}</small>}</label>
        <label><span>새 비밀번호 확인</span><input type="password" name="passwordConfirm" value={values.passwordConfirm} onChange={change} onBlur={touch} disabled={submitting} required minLength={8} maxLength={72} autoComplete="new-password" aria-invalid={Boolean(touched.passwordConfirm && errors.passwordConfirm)} />{fieldError('passwordConfirm')}</label>
        <p className="my-page-notice">변경하면 모든 기기에서 로그아웃되며, 새 비밀번호로 다시 로그인해야 합니다.</p>
        {error && <p className="my-page-field-error" role="alert">{error}</p>}
        <div className="my-page-form-actions"><button type="button" className="my-page-button" disabled={submitting} onClick={onCancel}>취소</button><button type="submit" className="my-page-button primary" disabled={submitting}>{submitting ? '변경 중…' : '비밀번호 변경'}</button></div>
    </form>;
}

PasswordForm.propTypes = {
    onCancel: PropTypes.func.isRequired,
};
